import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';
import dayjs from 'dayjs';
import '../Groups.less';
import { Form, Input, TimePicker, Select, Spin } from 'antd';
import NewCustomButton from '@/Components/Common/NewCustomButton';
import { ButtonTypes } from '@/Components/Common/CustomButton';
import {
  CloseIcon,
  NewCloseIcon,
  NewUpdateIcon,
  AttachmentLinkIcon,
  EditIcon,
  DeleteIcon,
  GroupsIcon,
} from '@/Components/Common/Icons';
import { SingleDatePicker, HSDayPicker } from '@/Components/Common/HSWidget';
import CustomDateRangePicker from '@/Components/Common/CustomDateRangePicker';
import { apiCallback } from '@vantiva-sa/homesight_humaui/Services/Common';
import { translator } from '@vantiva-sa/homesight_humaui/Store/configStore';
import { DateFormat, GroupModalActions } from '@vantiva-sa/homesight_humaui/Utilities/Constants';
import { convertToUserTimezone } from '@/utilities/helpers/DateTimeHelpers';

const AddEditCall = ({
  closeGroupModal,
  groupModalAction,
  userInfo,
  eventRecord = null,
  userTimezone,
  record,
  setRefreshSchedulePage,
  groupCallDeleteShowHandler,
  setDeleteScheduleCallObj,
  closeGroupModalHandler,
  isStaffAdmin,
  isStaff,
  setLatestGroupInfo = () => {},
}) => {
  const [form] = Form.useForm();
  const trans = useSelector(translator);
  const scheduleApi = useSelector((state) => state.apis.scheduleApi);
  const [scheduleCallName, setScheduleCallName] = useState('');

  const isEditForm = useMemo(
    () => (groupModalAction === GroupModalActions.editScheduleCall ? true : false),
    [groupModalAction]
  );
  const LocalTimezone = useMemo(
    () => userTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
    [userTimezone]
  );
  const localEvent = useMemo(
    () => (isEditForm ? revertUTCtoUserTimezone(eventRecord, LocalTimezone) : null),
    [isEditForm, eventRecord, LocalTimezone]
  );

  const CallTypes = [
    { value: 'schedule', label: trans('VIDEOCALL') },
    { value: 'zoom', label: trans('ZOOMCALL') },
  ];

  const RepeatOptions = [
    { value: 'none', label: trans('DOESNOTREPEAT') },
    { value: 'daily', label: trans('DAILY') },
    { value: 'weekly', label: trans('WEEKLY') },
    { value: 'custom', label: trans('CUSTOM') },
  ];

  useEffect(() => {
    setScheduleCallName(formData?.event_name);
  }, [isEditForm]);

  const [processing, setProcessing] = useState(false);

  // Initialize states based on isEditForm - only use eventRecord data when editing
  const [selectedDate, setSelectedDate] = useState('');

  const [checkedDays, setCheckedDays] = useState(() => {
    return isEditForm && eventRecord?.days_of_week ? eventRecord.days_of_week : [];
  });

  const [selectedRangeDates, setSelectedRangeDates] = useState(() => {
    if (isEditForm && localEvent?.startDate) {
      if (localEvent?.endDate) {
        return [
          dayjs(localEvent.startDate, 'YYYY-MM-DD').startOf('day'),
          dayjs(localEvent.endDate, 'YYYY-MM-DD').endOf('day'),
        ];
      } else {
        return [
          dayjs(localEvent.startDate, 'YYYY-MM-DD').startOf('day'),
          dayjs(localEvent.startDate, 'YYYY-MM-DD').endOf('day'),
        ];
      }
    }
    return [];
  });

  function revertUTCtoUserTimezone(eventRecord, userTimezone) {
    const startDateTime = convertToUserTimezone(
      eventRecord?.event_startdate,
      eventRecord?.event_starttime,
      userTimezone,
      'full'
    );
    const endDate = convertToUserTimezone(
      eventRecord?.event_enddate ?? eventRecord?.event_startdate,
      eventRecord?.event_starttime,
      userTimezone,
      'date'
    );
    const endTime = convertToUserTimezone(
      eventRecord?.event_startdate,
      eventRecord?.event_endtime,
      userTimezone,
      'time'
    );
    return {
      startDate: startDateTime.date,
      startTime: startDateTime.time,
      endDate: endDate,
      endTime: endTime,
    };
  }

  // Initialize form data - only use eventRecord when editing
  const getInitialFormData = () => {
    if (isEditForm && eventRecord) {
      return {
        repeat: eventRecord?.recurring ? eventRecord?.repeat : 'none',
        callType: eventRecord?.event_type || 'schedule',
        url: eventRecord?.meeting_url ?? '',
        event_name: eventRecord?.event_name || '',
        event_description: eventRecord?.event_description || '',
        starttime: localEvent ? dayjs(localEvent.startTime, 'HH:mm:ss') : '',
        endtime: localEvent ? dayjs(localEvent.endTime, 'HH:mm:ss') : '',
      };
    } else {
      // Fresh form data for create mode
      return {
        repeat: 'none',
        callType: 'schedule',
        url: '',
        event_name: '',
        event_description: '',
        starttime: '',
        endtime: '',
      };
    }
  };

  const [formData, setFormData] = useState(getInitialFormData());
  const [isCreateButtonDisabled, setIsCreateButtonDisabled] = useState(true);

  /* Reset form when switching from edit to create mode */
  useEffect(() => {
    if (!isEditForm) {
      const freshFormData = {
        repeat: 'none',
        callType: 'schedule',
        url: '',
        event_name: '',
        event_description: '',
        starttime: '',
        endtime: '',
      };
      setFormData(freshFormData);
      setCheckedDays([]);
      setSelectedRangeDates([]);
      setSelectedDate(dayjs().tz(userTimezone).startOf('day'));
      form.resetFields();
      form.setFieldsValue(freshFormData);
    }
  }, [isEditForm, form]);

  const get_utc_offset = (timeZone) => {
    return Intl.DateTimeFormat(navigator.language, {
      timeZoneName: 'longOffset',
      timeZone,
    })
      .formatToParts()
      .find((i) => i.type === 'timeZoneName').value;
  };
  const timezonedisplay = LocalTimezone + ` (${get_utc_offset(LocalTimezone)})`;

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData({ ...formData, [name]: value });
  };

  useEffect(() => {
    if (isEditForm && eventRecord) {
      const eventStartDateObj = convertToUserTimezone(
        eventRecord?.event_startdate,
        eventRecord?.event_starttime,
        userTimezone,
        'full'
      );
      const eventStartDate = dayjs(eventStartDateObj?.date).startOf('day');
      setSelectedDate(eventStartDate);
    }
  }, [eventRecord, isEditForm, userTimezone]);

  const handleDateSelect = (date) => {
    if (!date) {
      console.error('No date passed to handleDateSelect:', date);
      return;
    }
    const dayjsDate = dayjs.isDayjs(date) ? date : dayjs(date);
    if (!dayjsDate.isValid()) {
      console.error('Invalid date passed to handleDateSelect:', date);
      return;
    }
    setSelectedDate(dayjsDate);
  };

  const handleDateChange = (dates) => {
    setSelectedRangeDates(dates);
  };

  const handleValueChange = (name, value) => {
    if (name === 'starttime') {
      const newEndTime = value ? value.add(1, 'hour') : null;
      setFormData((prev) => ({ ...prev, starttime: value, endtime: newEndTime }));
      form.setFieldsValue({ endtime: newEndTime });
    } else if (name === 'repeat') {
      setCheckedDays([]);
      setFormData((prev) => ({ ...prev, [name]: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleOnDayChange = useCallback((days) => {
    setCheckedDays((prev) => {
      if (prev.length === days.length && prev.every((val, idx) => val === days[idx])) {
        return prev;
      }
      return days;
    });
  }, []);

  useEffect(() => {
    const { event_name, starttime, endtime, callType, repeat, url } = formData;
    const normalizedRepeat = repeat === trans('DOESNOTREPEAT') ? 'none' : repeat || 'none';

    const areRequiredFieldsFilled = !!(
      event_name?.trim() &&
      starttime &&
      endtime &&
      callType &&
      selectedDate
    );

    const isRepeatValid =
      normalizedRepeat === 'weekly'
        ? checkedDays?.length === 1 && selectedRangeDates?.length === 2
        : normalizedRepeat === 'custom'
          ? checkedDays?.length > 0 && selectedRangeDates?.length === 2
          : normalizedRepeat === 'daily'
            ? selectedRangeDates?.length === 2
            : true;

    const zoomUrlPattern = /https:\/\/[\w-]*\.?zoom.us\/(j|my)\/[\d\w?=-]+/;
    const isZoomUrlValid = callType === 'zoom' ? zoomUrlPattern.test(url.trim()) : true;

    setIsCreateButtonDisabled(!(areRequiredFieldsFilled && isRepeatValid && isZoomUrlValid));
  }, [formData, selectedDate, checkedDays, selectedRangeDates]);

  const handleSendClick = () => {
    form
      .validateFields()
      .then(() => onFormSubmit())
      .catch((error) => {
        console.error('Form Field Error', error);
        // message.error("Please fill in all required fields")
      });
  };

  const onFormSubmit = () => {
    const errorCallback = (error) => {
      setProcessing(false);
      const msg = `Error ${isEditForm ? 'Updating' : 'Creating'} Call!`;
      // message.error(msg);
      console.error(msg, error);
      closeGroupModalHandler();
    };
    const successCallback = (data) => {
      setProcessing(false);
      setLatestGroupInfo(); // Upating group info on updation or creation

      // Reset form fields after successful API call
      if (!isEditForm) {
        // Reset form
        form.resetFields();

        // Reset all state variables to initial values
        const initialFormData = {
          repeat: 'none',
          callType: 'schedule',
          url: '',
          event_name: '',
          event_description: '',
          starttime: '',
          endtime: '',
        };

        setFormData(initialFormData);
        setScheduleCallName('');
        setSelectedDate(dayjs().tz(userTimezone).startOf('day'));
        setCheckedDays([]);
        setSelectedRangeDates([]);
        setIsCreateButtonDisabled(true);

        // Set form field values to initial state
        form.setFieldsValue(initialFormData);
      }

      setRefreshSchedulePage((prev) => !prev);
      closeGroupModalHandler();
    };

    try {
      setProcessing(true);
      const startTime24 = dayjs(formData?.starttime).format('HH:mm');
      const endTime24 = dayjs(formData?.endtime).format('HH:mm');
      const formattedDates = selectedRangeDates.map((date) =>
        moment(date.$d).tz(userTimezone).format('YYYY-MM-DD')
      );

      const opts = {
        createEvent: {
          groupuuid: record?.groupuuid,
          event_type: formData?.callType,
          ...(formData?.callType === 'zoom' && { zoom: { url: formData?.url } }),
          event_name: formData?.event_name,
          event_description: formData?.event_description,
          event_startdate: selectedDate
            ? dayjs(selectedDate).tz(userTimezone).format('YYYY-MM-DD')
            : null,
          ...(formData?.repeat &&
            formData?.repeat !== 'none' &&
            formData?.repeat !== "Doesn't repeat" && {
              repeat: formData?.repeat,
              days_of_week: checkedDays,
              event_startdate: formattedDates[0],
              event_enddate: formattedDates[1],
            }),
          timezone: timezonedisplay,
          event_starttime: startTime24,
          event_endtime: endTime24,
          ...(isEditForm && { schedule_eventuuid: eventRecord?.schedule_eventuuid }),
          ...(!isEditForm && { contacts: record.invited_contacts }),
        },
      };

      if (isEditForm) {
        scheduleApi.udpateEvent(
          userInfo?.useruuid,
          eventRecord?.schedule_eventuuid,
          opts,
          apiCallback({
            failCallback: errorCallback,
            errorCallback,
            successCallback,
          })
        );
      } else {
        scheduleApi.createEvent(
          userInfo?.useruuid,
          opts,
          apiCallback({
            failCallback: errorCallback,
            errorCallback,
            successCallback,
          })
        );
      }
    } catch (error) {
      errorCallback(error);
    }
  };

  const scheduleCallDeleteHandler = () => {
    setDeleteScheduleCallObj({
      schedule_eventuuid: eventRecord?.schedule_eventuuid,
      recurring: eventRecord?.recurring,
      useruuid: userInfo?.useruuid,
      groupuuid: eventRecord?.groupuuid,
    });
    groupCallDeleteShowHandler();
  };

  const FormItemLayout = {
    labelCol: { span: 24 },
    wrapperCol: { span: 24 },
  };

  const getFormText = (type) => {
    const texts = {
      header: trans('CALLDETAILS'),
      placeholder: trans('ADDANAME'),
    };
    return texts[type];
  };

  const getModalHeaderOnActions = () => {
    return (
      <div className="form-title">
        <span>{isEditForm ? trans('EDITCALL') : trans('CREATECALL')}</span>
      </div>
    );
  };

  const renderDatePicker = () => {
    if (formData?.repeat === 'none' || formData?.repeat === "Doesn't repeat") {
      return (
        <Form.Item label={trans('DATE*')} name="date">
          <SingleDatePicker
            DateFormat={DateFormat}
            timeZone={userTimezone}
            defaultValue={selectedDate}
            onDateSelect={handleDateSelect}
            value={selectedDate}
          />
        </Form.Item>
      );
    } else {
      return (
        <Form.Item label={trans('DATE*')} name="date" initialValues={{ date: [] }}>
          <CustomDateRangePicker
            onDateChange={handleDateChange}
            selectedDates={selectedRangeDates}
            disabled={isEditForm}
          />
        </Form.Item>
      );
    }
  };

  return (
    <div className="callform-container">
      <Spin size="large" spinning={processing} tip={trans('PROCESSING')}>
        <Form
          form={form}
          initialValues={formData}
          {...FormItemLayout}
          className="taskform-container"
        >
          <div className="taskform-card">
            <div className="task-title schedule-call-title">
              {getModalHeaderOnActions()}
              <div>
                <CloseIcon
                  style={{ height: '20px', width: '20px', cursor: 'pointer', fill: '#4E758E' }}
                  onClick={() => closeGroupModal()}
                />
              </div>
            </div>
            {isEditForm && (
              <div className="group-schedule-call-header-sub-title">
                <div className="group-schedule-information">
                  <div className="groups-schedule-typegroup">
                    <GroupsIcon className="groups-schedule-typegroup-icon" />
                    <span className="groups-schedule-typegroup-label">{trans('GROUP')}</span>
                  </div>
                  <span className="groups-schedule-selected-label">{scheduleCallName}</span>
                </div>
              </div>
            )}

            <div className="form-items-container">
              <div className="column-1" style={{ width: '100%' }}>
                <span className="general-info-text">{getFormText('header')}</span>
                <div className="column-1-border">
                  <div className="form-group" style={{ marginTop: '0px' }}>
                    <Form.Item label={trans('NAMETHECALL*')} name="event_name">
                      <Input
                        placeholder={getFormText('placeholder')}
                        name="event_name"
                        onChange={handleInputChange}
                        value={formData?.event_name}
                      />
                    </Form.Item>
                  </div>

                  <Form.Item
                    label={trans('CALLTYPE*')}
                    name="callType"
                    className="form-group-select"
                  >
                    <Select
                      disabled={isEditForm}
                      onChange={(value) => handleValueChange('callType', value)}
                      value={formData?.callType}
                      name="callType"
                      className="select-type"
                      options={CallTypes}
                    />
                  </Form.Item>

                  {formData?.callType === 'zoom' && (
                    <div className="event-zoom-container">
                      <div className="zoom-card">
                        <div className="form-group" style={{ marginTop: '30px' }}>
                          <Form.Item
                            label=""
                            name="url"
                            rules={[
                              {
                                required: true,
                                message: trans('PLEASEENTERTHEURL'),
                              },
                              {
                                pattern: /https:\/\/[\w-]*\.?zoom.us\/(j|my)\/[\d\w?=-]+/,
                                message: trans('PLEASEENTERAVALIDZOOMMEETINGURL'),
                              },
                            ]}
                          >
                            <Input
                              placeholder={trans('PASTEMEETINGLINKHERE')}
                              className="zoom-url-display"
                              style={{ width: '100%' }}
                              name="url"
                              onChange={(e) => handleInputChange(e)}
                              value={formData?.url}
                              prefix={isEditForm && <AttachmentLinkIcon />}
                              suffix={isEditForm && <EditIcon />}
                            />
                          </Form.Item>
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="form-row" style={{ marginTop: '25px' }}>
                    <Form.Item
                      name="repeat"
                      className="form-group-select"
                      label={trans('REPEATS*')}
                    >
                      <Select
                        onChange={(value) => handleValueChange('repeat', value)}
                        value={formData?.repeat}
                        name="repeat"
                        className="select-type"
                        options={RepeatOptions}
                        disabled={isEditForm}
                      />
                    </Form.Item>
                  </div>

                  {formData?.repeat === 'custom' && (
                    <div className="form-row flex-space-between">
                      <div
                        className="form-group flex-row"
                        style={{
                          gap: '16px',
                          alignItems: 'baseline',
                          marginBottom: '-30px',
                        }}
                      >
                        <Form.Item name="days_of_week">
                          <HSDayPicker
                            onDayChange={handleOnDayChange}
                            name="days_of_week"
                            days={checkedDays}
                            allowMultiple={true}
                            disabled={isEditForm}
                            repeatKey={formData?.repeat}
                          />
                        </Form.Item>
                      </div>
                    </div>
                  )}

                  {formData?.repeat === 'weekly' && (
                    <div className="form-row flex-space-between">
                      <div
                        className="form-group flex-row"
                        style={{
                          gap: '16px',
                          alignItems: 'baseline',
                          marginBottom: '-30px',
                        }}
                      >
                        <Form.Item name="days_of_week">
                          <HSDayPicker
                            onDayChange={handleOnDayChange}
                            name="days_of_week"
                            days={checkedDays}
                            allowMultiple={false}
                            disabled={isEditForm}
                          />
                        </Form.Item>
                      </div>
                    </div>
                  )}

                  {renderDatePicker()}

                  <div
                    className="form-group"
                    style={{ display: 'flex', marginTop: '0px', gap: '30px' }}
                  >
                    <Form.Item
                      label={trans('STARTTIME*')}
                      name="starttime"
                      rules={[
                        {
                          type: 'object',
                          message: trans('PLEASESELECTTIME'),
                        },
                      ]}
                    >
                      <TimePicker
                        name="starttime"
                        placeholder={trans('SELECTTIME')}
                        style={{ width: '27rem' }}
                        allowClear={false}
                        required={true}
                        format={userInfo?.is24hoursformat ? 'HH:mm' : 'h:mm a'}
                        size="large"
                        onChange={(value) => handleValueChange('starttime', value)}
                        minuteStep={15}
                        suffixIcon={<span style={{ color: 'black' }}>{timezonedisplay}</span>}
                      />
                    </Form.Item>
                    <Form.Item
                      label={trans('ENDTIME*')}
                      name="endtime"
                      rules={[
                        {
                          type: 'object',
                          message: trans('PLEASESELECTTIME'),
                        },
                      ]}
                    >
                      <TimePicker
                        name="endtime"
                        placeholder={trans('SELECTTIME')}
                        style={{ width: '27rem' }}
                        allowClear={false}
                        required={true}
                        format={userInfo?.is24hoursformat ? 'HH:mm' : 'h:mm a'}
                        size="large"
                        onChange={(value) => handleValueChange('endtime', value)}
                        minuteStep={15}
                        disabled={!formData?.starttime}
                        disabledTime={(current) => {
                          if (!formData?.starttime) return {};
                          const start = formData?.starttime;
                          const startHour = start.hour();
                          const startMinute = start.minute();
                          const disabledHours = () => {
                            const hours = [];
                            for (let i = 0; i < startHour; i++) {
                              hours.push(i);
                            }
                            return hours;
                          };
                          const disabledMinutes = (selectedHour) => {
                            if (selectedHour === startHour) {
                              const minutes = [];
                              for (let i = 0; i <= startMinute; i++) {
                                minutes.push(i);
                              }
                              return minutes;
                            }
                            return [];
                          };
                          return {
                            disabledHours,
                            disabledMinutes,
                          };
                        }}
                        suffixIcon={
                          <span
                            style={{
                              color: !formData?.starttime ? 'rgba(0, 0, 0, 0.25)' : 'black',
                            }}
                          >
                            {timezonedisplay}
                          </span>
                        }
                      />
                    </Form.Item>
                  </div>

                  <div className="form-group">
                    <Form.Item name="event_description" label={trans('DESCRIPTION')}>
                      <Input.TextArea
                        placeholder={trans('ADDDESCRIPTION')}
                        name="event_description"
                        onChange={handleInputChange}
                        value={formData?.event_description}
                        className="form-description"
                      />
                    </Form.Item>
                  </div>

                  <div className="manadatory-text">{trans('*THESEFIELDSAREMANDATORY')}</div>
                </div>
              </div>
            </div>
            <div className="callform-action-button-container">
              <Form.Item>
                {(isEditForm && isStaffAdmin) ||
                (isEditForm &&
                  isStaff &&
                  userInfo?.useruuid === eventRecord?.event_originator_id) ? (
                  <NewCustomButton
                    className="cancel-form-icon delete-form-icon"
                    label={trans('DELETECALL')}
                    icon={<DeleteIcon style={{ fill: '#113E57' }} />}
                    onClick={scheduleCallDeleteHandler}
                  />
                ) : null}
              </Form.Item>
              <Form.Item>
                <NewCustomButton
                  className="cancel-form-icon"
                  label={trans('CANCEL')}
                  icon={<NewCloseIcon style={{ fill: '#113E57' }} className="cancel-icon" />}
                  onClick={closeGroupModalHandler}
                />
              </Form.Item>
              <Form.Item>
                <NewCustomButton
                  disabled={isCreateButtonDisabled}
                  type={ButtonTypes.primary}
                  label={isEditForm ? trans('UPDATE') : trans('CREATE')}
                  icon={
                    <NewUpdateIcon
                      style={{ fill: '#003252' }}
                      className={
                        !isCreateButtonDisabled ? 'update-icon-enabled' : 'update-icon-disabled'
                      }
                    />
                  }
                  onClick={handleSendClick}
                />
              </Form.Item>
            </div>
          </div>
        </Form>
      </Spin>
    </div>
  );
};

export default AddEditCall;
